import { Button, Text } from "@chakra-ui/react";
import { Table, TableProps } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import { useGetApplicants } from "../useCases/useGetApplicants";
import { useEffect, useMemo, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { LuPlus } from "react-icons/lu";
import { useCreateApplicant } from "../useCases/createApplicant";
import { useUpdateApplicant } from "../useCases/useUpdateApplicant";
import { UpdateApplicantModal } from "./UpdateApplicantModal";
import { CreateApplicantModal } from "./CreateApplicantModal";
import { Applicant } from "@/shared/core-vouchers";
import { ApplicantInput } from "../types/Applicant";
import { useAuth } from "@/shared/session";
import { Pagination } from "@/shared/core-configs";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { PageContainer } from "@/ui/containers/PageContainer";
import { CreatorCell } from "@/core/vouchers/Columns";
import { useGetCompanies } from "@/core/companies/useCases/useGetCompanies";
import { TableFilters } from "@/ui/filters/TableFilters";
import {
  countActiveFilters,
  getOption,
  type FilterField,
  type FilterValues,
} from "@/ui/filters/filterValues";

const getColumns = ({
  handleToggleEnabled,
}: {
  handleToggleEnabled: (applicant: Applicant) => void;
}): TableProps<Applicant>["headers"] => [
  {
    label: "Nombre",
    itemLabel: (item) => <Text fontWeight="semibold">{item.name}</Text>,
    sortable: true,
    sortKey: "name",
  },
  {
    label: "Correo",
    sortable: true,
    sortKey: "email",
    itemLabel: (item: Applicant) => item.email || "---",
  },
  {
    label: "Teléfono",
    itemLabel: "phone",
  },

  {
    label: "Empresa",
    itemLabel: (item: Applicant) => item.company?.name,
  },
  {
    label: "Creado por",
    itemLabel: (item: Applicant) => <CreatorCell name={item.creator?.name} />,
  },
  {
    label: "Estado",
    itemLabel: (item: Applicant) => (
      <Button
        variant="ghost"
        size="sm"
        colorPalette={item.enabled ? "gray" : "green"}
        color={item.enabled ? "fg.muted" : undefined}
        onClick={() => handleToggleEnabled(item)}
      >
        {item.enabled ? "Deshabilitar" : "Habilitar"}
      </Button>
    ),
  },
];

export const ApplicantTable = () => {
  const { session } = useAuth();

  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });

  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });
  const { mutate: createApplicant } = useCreateApplicant();
  const { mutate: updateApplicant } = useUpdateApplicant();
  const [showDisabled, setShowDisabled] = useState(false);

  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(
    null,
  );
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { data: companies } = useGetCompanies();
  const filterFields = useMemo<FilterField[]>(
    () => [
      {
        type: "select",
        key: "company",
        label: "Empresa",
        placeholder: "Todas",
        options: companies?.map((c) => ({ value: c.id, label: c.name })),
      },
    ],
    [companies],
  );
  const [extraFilters, setExtraFilters] = useState<FilterValues>({});
  const companyId = getOption(extraFilters, "company")?.value;

  const {
    data: applicants = [],
    refetch,
    isPending,
    pagination: metaApplicants,
  } = useGetApplicants({
    showDisabled,
    zoneIds: session?.zone ?? undefined,
    page: pagination.page,
    ...order,
    pageSize: pagination.pageSize,
    searchTerm: debouncedSearchTerm,
    companyId,
  });

  const handleOpenCreateModal = () => setCreateModalOpen(true);
  const handleCloseCreateModal = () => setCreateModalOpen(false);

  const handleOpenEditModal = (applicant: Applicant) => {
    setSelectedApplicant(applicant);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedApplicant(null);
  };

  const handleCreateApplicant = (data: ApplicantInput) => {
    createApplicant(data, {
      onSuccess: () => {
        setCreateModalOpen(false);
        notify.success("Solicitante creado");
        refetch();
      },
      onError: (error) =>
        notify.error("No se pudo crear el solicitante", error),
    });
  };

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = async (applicant: Applicant) => {
    const ok = await confirmToggle(
      applicant.enabled,
      `al solicitante ${applicant.name}`,
    );
    if (!ok) return;
    const updatedApplicant: ApplicantInput = {
      name: applicant.name,
      phone: applicant.phone,
      email: applicant.email,
      companyId: applicant.company.id,
      zoneId: applicant.zone.id,
      enabled: !applicant.enabled,
    };

    updateApplicant(
      { id: applicant.id, updatedApplicant },
      {
        onSuccess: () => {
          notify.success(
            applicant.enabled
              ? "Solicitante deshabilitado"
              : "Solicitante habilitado",
          );
          refetch();
        },
        onError: (error) =>
          notify.error("No se pudo actualizar el solicitante", error),
      },
    );
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, companyId, showDisabled]);

  const handleOnSortChange = (sort: string, order: string) => {
    setOrder({ sort, order });
  };
  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Solicitantes"
      description="Registra y gestiona a los solicitantes con sus datos básicos."
      actions={
        <Button colorPalette="brand" onClick={handleOpenCreateModal}>
          <LuPlus />
          Crear solicitante
        </Button>
      }
    >
      <Table
        searchPlaceholder="Buscar por nombre o correo…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        isLoading={isPending}
        page={pagination.page}
        hasActiveFilters={countActiveFilters(extraFilters) > 0}
        onClearFilters={() => setExtraFilters({})}
        filters={
          <TableFilters
            fields={filterFields}
            value={extraFilters}
            onChange={setExtraFilters}
          />
        }
        emptyDescription="Los solicitantes son las personas que reciben los productos de un vale."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleOpenCreateModal}
          >
            Crear solicitante
          </Button>
        }
        headers={getColumns({ handleToggleEnabled })}
        data={applicants}
        extractItemKey={(item: Applicant) => item.id}
        onEdit={(item: Applicant) => handleOpenEditModal(item)}
        showPagination={true}
        pages={metaApplicants?.totalPages}
        totalItems={metaApplicants?.count}
        itemsLabel="solicitantes"
        onShowDisabledChange={setShowDisabled}
        onSortChange={handleOnSortChange}
        showDisabledCheck
        onSearch={setSearchTerm}
        onChangePage={(page) =>
          setPagination({ ...pagination, page: page + 1 })
        }
        hiddenColumnsKey="applicants"
      />
      <CreateApplicantModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateApplicant}
      />

      {selectedApplicant && (
        <UpdateApplicantModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          applicant={selectedApplicant}
        />
      )}
    </PageContainer>
  );
};
